import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { MessageCircle, X, Send, Phone, Mail } from 'lucide-react';

const LOGO_URL = '/logo-givinghopes.png';

export default function SupportChat() {
  const { apiClient } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: 'assistant', content: "Hi! I'm the Giving Hope support assistant. How can I help you today?" },
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    const userMsg = input.trim();
    setInput('');
    setMessages((prev) => [...prev, { role: 'user', content: userMsg }]);
    setLoading(true);

    try {
      const { data } = await apiClient.post('/api/chat', {
        message: userMsg,
        session_id: sessionId,
      });
      setSessionId(data.session_id);
      setMessages((prev) => [...prev, { role: 'assistant', content: data.response }]);
    } catch {
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: "I'm having trouble connecting right now. Please call us at (410) 505-8875 or email admin@givinghopes.com for immediate help.",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Hide on admin pages */}
      {location.pathname.startsWith('/admin') ? null : (
        <>
          {/* Floating Button */}
          {!open && (
            <button
              onClick={() => setOpen(true)}
              className="fixed bottom-20 right-6 z-[9999] w-14 h-14 rounded-full bg-[var(--primary-blue)] text-white shadow-lg shadow-blue-500/30 flex items-center justify-center hover:scale-110 transition-transform"
              data-testid="support-chat-toggle"
            >
              <MessageCircle size={24} />
            </button>
          )}

          {/* Chat Window */}
          {open && (
        <div className="fixed bottom-6 right-6 z-[9999] w-[380px] max-w-[calc(100vw-2rem)] h-[520px] max-h-[calc(100vh-6rem)] flex flex-col rounded-2xl overflow-hidden shadow-2xl border border-[var(--border-color)]" data-testid="support-chat-window">
          {/* Header */}
          <div className="bg-[var(--primary-blue)] text-white px-5 py-4 flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="Logo" className="w-8 h-8 object-contain" />
              <div>
                <p className="font-heading font-bold text-sm">Giving Hope Support</p>
                <p className="text-[10px] text-white/70">Powered by AI &bull; Available 24/7</p>
              </div>
            </div>
            <button onClick={() => setOpen(false)} className="p-1 hover:bg-white/20 rounded-lg transition-colors" data-testid="support-chat-close">
              <X size={18} />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-[var(--bg-alt)]" data-testid="chat-messages">
            {messages.map((msg, i) => (
              <div key={`msg-${i}-${msg.role}`} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div
                  className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
                    msg.role === 'user'
                      ? 'bg-[var(--primary-blue)] text-white rounded-br-sm'
                      : 'bg-white border border-[var(--border-color)] text-[var(--text-body)] rounded-bl-sm'
                  }`}
                >
                  {msg.content}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-white border border-[var(--border-color)] px-4 py-3 rounded-2xl rounded-bl-sm">
                  <div className="flex gap-1">
                    <div className="w-2 h-2 bg-[var(--text-body)]/30 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <div className="w-2 h-2 bg-[var(--text-body)]/30 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <div className="w-2 h-2 bg-[var(--text-body)]/30 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Quick contact */}
          <div className="bg-white border-t border-[var(--border-color)] px-4 py-2 flex gap-3 text-[10px] text-[var(--text-body)] flex-shrink-0">
            <a href="tel:4105058875" className="flex items-center gap-1 hover:text-[var(--primary-blue)]"><Phone size={10} /> (410) 505-8875</a>
            <a href="mailto:admin@givinghopes.com" className="flex items-center gap-1 hover:text-[var(--primary-blue)]"><Mail size={10} /> admin@givinghopes.com</a>
          </div>

          {/* Input */}
          <form onSubmit={sendMessage} className="bg-white border-t border-[var(--border-color)] px-4 py-3 flex gap-2 flex-shrink-0">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Type your question..."
              className="flex-1 px-3 py-2 rounded-xl bg-[var(--bg-alt)] border border-[var(--border-color)] text-sm focus:outline-none focus:ring-1 focus:ring-[var(--primary-blue)]"
              data-testid="chat-input"
            />
            <button
              type="submit"
              disabled={loading || !input.trim()}
              className="w-10 h-10 rounded-xl bg-[var(--primary-blue)] text-white flex items-center justify-center disabled:opacity-40 hover:shadow-lg transition-all"
              data-testid="chat-send-btn"
            >
              <Send size={16} />
            </button>
          </form>
        </div>
      )}
        </>
      )}
    </>
  );
}
